import React, { useEffect, useState, useCallback, useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import { fmtNum, fmtMoney } from "@/lib/format";
import { useCurrency } from "@/context/CurrencyContext";
import StatCard from "@/components/StatCard";
import { Loading, EmptyState } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { toast } from "sonner";
import { downloadCSV } from "@/lib/csv";
import ProductThumb, { imgSrc } from "@/components/ProductThumb";
import { Warehouse, Boxes, Truck, AlertTriangle, Plus, Pencil, Trash2, X, Link2, Upload, Download, Image as ImageIcon, Check, ChevronsUpDown, Loader2, LayoutGrid, List, BarChart3, Layers, ShoppingCart } from "lucide-react";
import WarehouseAnalyticsDrawer from "@/components/WarehouseAnalyticsDrawer";

const blank = { warehouse_sku: "", name: "", stock_level: "", unit_cost: "", image_url: "", linked_skus: [], variation_group: null };
const SALE_CURRENCIES = ["USD", "GBP", "EUR", "CAD", "MXN", "SEK"];
const blankSale = { channel: "", warehouse_sku: "", quantity: "", sale_price: "", currency: "USD", date: "", note: "" };

function LinkedSkuCombobox({ knownSkus, selected, onAdd }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const available = knownSkus.filter((s) => !selected.includes(s));
  const q = query.trim();
  const showCustom = q && !knownSkus.some((s) => s.toLowerCase() === q.toLowerCase()) && !selected.includes(q);
  const pick = (sku) => { onAdd(sku); setQuery(""); setOpen(false); };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button data-testid="wh-link-dropdown" type="button" variant="outline" role="combobox"
          className="w-full justify-between bg-[#111] border-[#262626] text-white hover:bg-[#1a1a1a] hover:text-white font-normal">
          <span className="text-[#A3A3A3]">Select or type an Amazon SKU…</span>
          <ChevronsUpDown className="h-4 w-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 bg-[#0d0d0d] border-[#262626] text-white w-[--radix-popover-trigger-width]" align="start">
        <Command className="bg-[#0d0d0d]" filter={(value, search) => (value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0)}>
          <CommandInput data-testid="wh-link-search" value={query} onValueChange={setQuery} placeholder="Search SKUs…" className="text-white" />
          <CommandList>
            {!available.length && !showCustom && <CommandEmpty className="text-[#666] text-xs py-4 text-center">No matching SKUs.</CommandEmpty>}
            {showCustom && (
              <CommandGroup heading="Custom">
                <CommandItem data-testid="wh-link-custom" value={q} onSelect={() => pick(q)} className="text-white aria-selected:bg-[#007AFF]/20">
                  <Plus className="h-3.5 w-3.5 mr-2 text-[#007AFF]" /> Add “{q}”
                </CommandItem>
              </CommandGroup>
            )}
            {available.length > 0 && (
              <CommandGroup heading="Known SKUs">
                {available.map((s) => (
                  <CommandItem key={s} data-testid={`wh-link-opt-${s}`} value={s} onSelect={() => pick(s)} className="text-white aria-selected:bg-[#007AFF]/20 tnum">
                    <Check className="h-3.5 w-3.5 mr-2 opacity-0" /> {s}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default function WarehouseTab() {
  const { currency, toDisplay } = useCurrency();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [knownSkus, setKnownSkus] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(blank);
  const [uploadingImg, setUploadingImg] = useState(false);
  const [view, setView] = useState("table");
  const [lowDays, setLowDays] = useState(14);
  const [groups, setGroups] = useState([]);
  const [groupBy, setGroupBy] = useState(false);
  const [analyticsSku, setAnalyticsSku] = useState(null);
  const [newGroup, setNewGroup] = useState("");
  const [salesOpen, setSalesOpen] = useState(false);
  const [sales, setSales] = useState([]);
  const [saleForm, setSaleForm] = useState(blankSale);
  const fileRef = useRef(null);
  const imgRef = useRef(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([api.get("/warehouse"), api.get("/warehouse/known-skus"), api.get("/variation-groups"), api.get("/other-channel-sales")])
      .then(([w, k, g, s]) => { setItems(w.data.data); setKnownSkus(k.data.skus); setGroups(g.data.data); setSales(s.data.data); setLoading(false); })
      .catch(() => { toast.error("Could not load warehouse."); setLoading(false); });
  }, []);
  useEffect(load, [load]);

  const openAdd = () => { setEditing(false); setForm(blank); setDialogOpen(true); };
  const openEdit = (it) => {
    setEditing(true);
    setForm({ warehouse_sku: it.warehouse_sku, name: it.name || "", stock_level: String(it.stock_level ?? ""), unit_cost: String(it.unit_cost ?? ""), image_url: it.image_url || "", linked_skus: it.linked_skus || [], variation_group: it.variation_group || null });
    setDialogOpen(true);
  };

  const addLink = (sku) => {
    const s = (sku || "").trim();
    if (!s || form.linked_skus.includes(s)) return;
    setForm((f) => ({ ...f, linked_skus: [...f.linked_skus, s] }));
  };
  const removeLink = (s) => setForm((f) => ({ ...f, linked_skus: f.linked_skus.filter((x) => x !== s) }));

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingImg(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/warehouse/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image_url: r.data.url }));
      toast.success("Image uploaded");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploadingImg(false); if (imgRef.current) imgRef.current.value = ""; }
  };

  const save = async () => {
    if (!form.warehouse_sku.trim()) { toast.error("Warehouse SKU is required."); return; }
    try {
      await api.post("/warehouse", {
        warehouse_sku: form.warehouse_sku.trim(),
        name: form.name.trim(),
        stock_level: Number(form.stock_level) || 0,
        unit_cost: Number(form.unit_cost) || 0,
        image_url: form.image_url.trim(),
        linked_skus: form.linked_skus,
      });
      await api.post(`/warehouse/${encodeURIComponent(form.warehouse_sku.trim())}/group`, { group_id: form.variation_group || null });
      toast.success(editing ? "Warehouse SKU updated" : "Warehouse SKU added");
      setDialogOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const createGroup = async () => {
    const n = newGroup.trim();
    if (!n) return;
    try {
      const r = await api.post("/variation-groups", { name: n });
      const g = await api.get("/variation-groups");
      setGroups(g.data.data);
      setForm((f) => ({ ...f, variation_group: r.data.id }));
      setNewGroup("");
      toast.success("Variation group created");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (it) => {
    try { await api.delete(`/warehouse/${it.id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Could not delete."); }
  };

  const saveSale = async () => {
    if (!saleForm.channel.trim()) { toast.error("Channel is required."); return; }
    if (!saleForm.warehouse_sku) { toast.error("Pick a warehouse SKU."); return; }
    if (!(Number(saleForm.quantity) >= 1)) { toast.error("Quantity must be at least 1."); return; }
    try {
      await api.post("/other-channel-sales", { ...saleForm, quantity: Number(saleForm.quantity), sale_price: Number(saleForm.sale_price) || 0 });
      toast.success("Sale recorded — warehouse stock adjusted");
      setSalesOpen(false); setSaleForm(blankSale); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const removeSale = async (id) => { try { await api.delete(`/other-channel-sales/${id}`); toast.success("Removed"); load(); } catch { toast.error("Could not delete."); } };

  const importCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/warehouse/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${r.data.imported} warehouse SKU(s)`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { if (fileRef.current) fileRef.current.value = ""; }
  };

  const exportCsv = () => downloadCSV("warehouse-stock.csv", items.map((it) => ({
    warehouse_sku: it.warehouse_sku, name: it.name || "", stock_level: it.stock_level ?? 0,
    shipped_out: it.shipped_out ?? 0, available: it.available ?? 0, unit_cost: it.unit_cost ?? 0,
    roi_pct: it.roi ?? "", linked_skus: (it.linked_skus || []).join("|"), image_url: imgSrc(it.image) || "",
  })));

  if (loading) return <Loading />;

  const rowNode = (it) => (
    <tr key={it.id} data-testid={`warehouse-row-${it.warehouse_sku}`} className="border-t border-[#141414] hover:bg-[#0b0b0b]">
      <td className="px-5 py-3"><ProductThumb src={it.image} size={40} title={it.name} /></td>
      <td className="px-5 py-3 tnum text-white">{it.warehouse_sku}</td>
      <td className="px-5 py-3 text-[#D4D4D4]">{it.name || "—"}</td>
      <td className="px-5 py-3">
        <div className="flex flex-wrap gap-1">
          {(it.linked_skus || []).map((s) => (
            <span key={s} className="px-1.5 py-0.5 rounded bg-[#007AFF]/15 text-[#4da3ff] text-[11px] tnum">{s}</span>
          ))}
          {!(it.linked_skus || []).length && <span className="text-[#666] text-xs">—</span>}
        </div>
      </td>
      <td className="px-5 py-3 text-right tnum text-[#D4D4D4]">{fmtNum(it.stock_level)}</td>
      <td className="px-5 py-3 text-right tnum text-[#D4D4D4]">{fmtNum(it.shipped_out)}</td>
      <td className="px-5 py-3 text-right tnum" style={{ color: it.other_channel_sold ? "#8B5CF6" : "#555" }}>{it.other_channel_sold ? fmtNum(it.other_channel_sold) : "—"}</td>
      <td className="px-5 py-3 text-right tnum font-semibold" style={{ color: availColor(it.available) }}>{fmtNum(it.available)}</td>
      <td className="px-5 py-3 text-right tnum text-[#D4D4D4]">{it.unit_cost ? fmtMoney(it.unit_cost) : "—"}</td>
      <td className="px-5 py-3 text-right tnum" style={{ color: it.roi == null ? "#555" : it.roi >= 0 ? "#10B981" : "#EF4444" }}>{it.roi == null ? "—" : `${it.roi}%`}</td>
      <td data-testid={`wh-cover-${it.warehouse_sku}`} className="px-5 py-3 text-right tnum" style={{ color: it.available <= 0 ? "#EF4444" : it.days_left == null ? "#555" : it.days_left <= lowDays ? "#F59E0B" : "#10B981" }}>{it.available <= 0 ? "Out" : it.days_left == null ? "—" : `~${it.days_left}d`}</td>
      <td className="px-5 py-3 text-right">
        <div className="inline-flex items-center gap-2">
          <button data-testid={`analyze-warehouse-${it.warehouse_sku}`} onClick={() => setAnalyticsSku(it.warehouse_sku)} className="text-[#A3A3A3] hover:text-[#10B981]" title="Analyze"><BarChart3 className="h-4 w-4" /></button>
          <button data-testid={`edit-warehouse-${it.warehouse_sku}`} onClick={() => openEdit(it)} className="text-[#A3A3A3] hover:text-[#007AFF]" title="Edit"><Pencil className="h-4 w-4" /></button>
          <button data-testid={`delete-warehouse-${it.warehouse_sku}`} onClick={() => remove(it)} className="text-[#A3A3A3] hover:text-[#EF4444]" title="Delete"><Trash2 className="h-4 w-4" /></button>
        </div>
      </td>
    </tr>
  );

  const buildGrouped = () => {
    const byGroup = new Map();
    const nameOf = (gid) => (groups.find((g) => g.id === gid)?.name) || "Ungrouped";
    for (const it of items) {
      const key = it.variation_group ? nameOf(it.variation_group) : "Ungrouped";
      if (!byGroup.has(key)) byGroup.set(key, []);
      byGroup.get(key).push(it);
    }
    const entries = Array.from(byGroup.entries());
    entries.sort((a, b) => (a[0] === "Ungrouped" ? 1 : b[0] === "Ungrouped" ? -1 : a[0].localeCompare(b[0])));
    return entries;
  };

  const totalAvailable = items.reduce((a, i) => a + (i.available || 0), 0);
  const totalShipped = items.reduce((a, i) => a + (i.shipped_out || 0), 0);
  const lowCount = items.filter((i) => (i.available || 0) <= 0).length;
  const availColor = (v) => (v <= 0 ? "#EF4444" : v <= 20 ? "#F59E0B" : "#10B981");
  const lowItems = items.filter((i) => i.available <= 0 || (i.days_left != null && i.days_left <= lowDays));

  return (
    <div className="space-y-6" data-testid="warehouse-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-[#A3A3A3] max-w-md">Track stock sitting in your warehouse (not yet shipped). Available auto-updates as units are dispatched to FBA.</p>
        <div className="flex items-center gap-2">
          <div className="flex items-center rounded-full border border-[#262626] bg-[#111] p-0.5">
            <button data-testid="wh-view-table" onClick={() => setView("table")} title="Table view" className={`h-8 w-8 rounded-full flex items-center justify-center transition-colors ${view === "table" ? "bg-[#007AFF] text-white" : "text-[#A3A3A3] hover:text-white"}`}><List className="h-4 w-4" /></button>
            <button data-testid="wh-view-gallery" onClick={() => setView("gallery")} title="Gallery view" className={`h-8 w-8 rounded-full flex items-center justify-center transition-colors ${view === "gallery" ? "bg-[#007AFF] text-white" : "text-[#A3A3A3] hover:text-white"}`}><LayoutGrid className="h-4 w-4" /></button>
          </div>
          <Button data-testid="wh-groupby-toggle" onClick={() => setGroupBy((v) => !v)} variant="outline" className={`h-9 rounded-full ${groupBy ? "bg-[#007AFF]/20 border-[#007AFF] text-white hover:bg-[#007AFF]/30 hover:text-white" : "bg-[#111] border-[#262626] text-white hover:bg-[#1a1a1a] hover:text-white"}`}>
            <Layers className="h-4 w-4 mr-2" /> {groupBy ? "Grouped" : "Group by variation"}
          </Button>
          <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={importCsv} className="hidden" data-testid="wh-import-input" />
          {!!items.length && (
            <Button data-testid="export-warehouse-button" onClick={exportCsv} variant="outline" className="h-9 bg-[#111] border-[#262626] text-white hover:bg-[#1a1a1a] hover:text-white rounded-full">
              <Download className="h-4 w-4 mr-2" /> Export CSV
            </Button>
          )}
          <Button data-testid="import-warehouse-button" onClick={() => fileRef.current?.click()} variant="outline" className="h-9 bg-[#111] border-[#262626] text-white hover:bg-[#1a1a1a] hover:text-white rounded-full">
            <Upload className="h-4 w-4 mr-2" /> Import CSV
          </Button>
          <Button data-testid="record-sale-button" onClick={() => { setSaleForm(blankSale); setSalesOpen(true); }} variant="outline" className="h-9 bg-[#111] border-[#262626] text-white hover:bg-[#1a1a1a] hover:text-white rounded-full">
            <ShoppingCart className="h-4 w-4 mr-2" /> Record Sale
          </Button>
          <Button data-testid="add-warehouse-button" onClick={openAdd} className="h-9 bg-[#007AFF] hover:bg-[#005BB5] rounded-full px-4 font-semibold">
            <Plus className="h-4 w-4 mr-1" /> Add Warehouse SKU
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testid="wh-skus" label="Warehouse SKUs" value={fmtNum(items.length)} sub="tracked products" icon={Warehouse} accent="#007AFF" />
        <StatCard testid="wh-available" label="Available Stock" value={fmtNum(totalAvailable)} sub="units on hand" icon={Boxes} accent="#10B981" />
        <StatCard testid="wh-shipped" label="Shipped Out" value={fmtNum(totalShipped)} sub="deducted by FBA" icon={Truck} accent="#8B5CF6" />
        <StatCard testid="wh-low" label="Out of Stock" value={fmtNum(lowCount)} sub="need restock" icon={AlertTriangle} accent="#EF4444" />
      </div>

      {lowItems.length > 0 && (
        <div data-testid="low-stock-banner" className="rounded-xl border border-[#F59E0B]/40 bg-[#F59E0B]/10 p-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 text-[#F59E0B]">
              <AlertTriangle className="h-4 w-4" />
              <p className="text-sm font-semibold">{lowItems.length} SKU{lowItems.length > 1 ? "s" : ""} need attention — projected to run out within {lowDays} days (or already out)</p>
            </div>
            <div className="flex items-center gap-1.5 text-xs">
              <span className="text-[#A3A3A3]">Alert window</span>
              {[7, 14, 30].map((d) => (
                <button key={d} data-testid={`low-days-${d}`} onClick={() => setLowDays(d)} className={`px-2.5 py-1 rounded-full font-semibold transition-colors ${lowDays === d ? "bg-[#F59E0B] text-black" : "text-[#F59E0B] border border-[#F59E0B]/40 hover:bg-[#F59E0B]/10"}`}>{d}d</button>
              ))}
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            {lowItems.slice(0, 10).map((it) => (
              <span key={it.id} data-testid={`low-chip-${it.warehouse_sku}`} className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-[#1a1a1a] text-xs">
                <span className="text-white tnum">{it.warehouse_sku}</span>
                <span className="tnum font-semibold" style={{ color: it.available <= 0 ? "#EF4444" : "#F59E0B" }}>{it.available <= 0 ? "OUT" : `~${it.days_left}d`}</span>
              </span>
            ))}
            {lowItems.length > 10 && <span className="text-xs text-[#A3A3A3] self-center">+{lowItems.length - 10} more</span>}
          </div>
        </div>
      )}

      {!items.length ? (
        <EmptyState title="No warehouse SKUs yet" message="Add a warehouse SKU, set its stock level, and link the Amazon/FBA SKU(s) that draw from it. As FBA shipments go out, available stock drops automatically." />
      ) : view === "gallery" ? (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="warehouse-gallery">
          {items.map((it) => (
            <div key={it.id} data-testid={`warehouse-card-${it.warehouse_sku}`} className="bg-[#0f0f0f] border border-white/5 rounded-xl p-4 flex flex-col gap-3 hover:border-white/10 transition-colors duration-150">
              <div className="flex items-center justify-center bg-[#0b0b0b] rounded-lg p-2 h-[140px]"><ProductThumb src={it.image} size={124} title={it.name} /></div>
              <div>
                <p className="text-white text-sm font-semibold truncate" title={it.name || it.warehouse_sku}>{it.name || it.warehouse_sku}</p>
                <p className="text-[#666] text-xs tnum">{it.warehouse_sku}</p>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-[#666]">Available</p>
                  <p className="text-xl font-bold tnum" style={{ color: availColor(it.available) }}>{fmtNum(it.available)}</p>
                </div>
                <div className="text-right">
                  <p className="text-[10px] uppercase tracking-widest text-[#666]">ROI</p>
                  <p className="text-sm font-semibold tnum" data-testid={`gallery-roi-${it.warehouse_sku}`} style={{ color: it.roi == null ? "#555" : it.roi >= 0 ? "#10B981" : "#EF4444" }}>{it.roi == null ? "—" : `${it.roi}%`}</p>
                </div>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-[#666]">Cover</span>
                <span className="tnum font-semibold" style={{ color: it.available <= 0 ? "#EF4444" : it.days_left == null ? "#555" : it.days_left <= lowDays ? "#F59E0B" : "#10B981" }}>{it.available <= 0 ? "Out of stock" : it.days_left == null ? "—" : `~${it.days_left} days`}</span>
              </div>
              <div className="flex items-center justify-between text-xs text-[#666] tnum">
                <span>Shipped {fmtNum(it.shipped_out)}</span>
                <span>{it.unit_cost ? fmtMoney(it.unit_cost) : "—"}</span>
              </div>
              <div className="flex items-center gap-4 pt-2 border-t border-[#141414]">
                <button data-testid={`gallery-analyze-${it.warehouse_sku}`} onClick={() => setAnalyticsSku(it.warehouse_sku)} className="inline-flex items-center gap-1 text-[#A3A3A3] hover:text-[#10B981] text-xs font-semibold transition-colors duration-150"><BarChart3 className="h-3.5 w-3.5" /> Analyze</button>
                <button data-testid={`gallery-edit-${it.warehouse_sku}`} onClick={() => openEdit(it)} className="inline-flex items-center gap-1 text-[#A3A3A3] hover:text-[#007AFF] text-xs font-semibold transition-colors duration-150"><Pencil className="h-3.5 w-3.5" /> Edit</button>
                <button data-testid={`gallery-delete-${it.warehouse_sku}`} onClick={() => remove(it)} className="inline-flex items-center gap-1 text-[#A3A3A3] hover:text-[#EF4444] text-xs font-semibold transition-colors duration-150"><Trash2 className="h-3.5 w-3.5" /> Delete</button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-[#0f0f0f] border border-white/5 rounded-xl overflow-hidden" data-testid="warehouse-table">
          <div className="p-5 border-b border-[#1a1a1a]"><h3 className="font-head font-bold text-white">Warehouse Stock</h3><p className="text-xs text-[#666]">Available = starting stock − units shipped to FBA</p></div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-[#666] text-xs uppercase tracking-wider">
                <th className="px-5 py-3">Product</th><th className="px-5 py-3">Warehouse SKU</th><th className="px-5 py-3">Name</th><th className="px-5 py-3">Linked Amazon SKUs</th>
                <th className="px-5 py-3 text-right">Starting</th><th className="px-5 py-3 text-right">Shipped</th><th className="px-5 py-3 text-right">Other Ch.</th><th className="px-5 py-3 text-right">Available</th><th className="px-5 py-3 text-right">Unit Cost</th><th className="px-5 py-3 text-right">ROI</th><th className="px-5 py-3 text-right">Cover</th><th className="px-5 py-3 text-right">Actions</th>
              </tr></thead>
              <tbody>
                {groupBy
                  ? buildGrouped().map(([gname, list]) => (
                      <React.Fragment key={gname}>
                        <tr data-testid={`wh-group-header-${gname}`} className="bg-[#111]">
                          <td colSpan={12} className="px-5 py-2 text-xs">
                            <span className="text-white font-semibold">{gname}</span>
                            <span className="text-[#666] ml-3">{list.length} SKU{list.length > 1 ? "s" : ""} · {fmtNum(list.reduce((a, i) => a + (i.available || 0), 0))} available · {fmtNum(list.reduce((a, i) => a + (i.shipped_out || 0), 0))} shipped</span>
                          </td>
                        </tr>
                        {list.map(rowNode)}
                      </React.Fragment>
                    ))
                  : items.map(rowNode)}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#0d0d0d] border-[#262626] text-white max-h-[90vh] overflow-y-auto" data-testid="warehouse-dialog">
          <DialogHeader>
            <DialogTitle className="font-head flex items-center gap-2"><Warehouse className="h-5 w-5 text-[#007AFF]" /> {editing ? "Edit Warehouse SKU" : "Add Warehouse SKU"}</DialogTitle>
            <DialogDescription className="text-[#A3A3A3]">Set stock, add a product image, and link the Amazon SKUs that draw from this warehouse item.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-[#A3A3A3]">Warehouse SKU</Label>
                <Input data-testid="wh-input-sku" value={form.warehouse_sku} disabled={editing} onChange={(e) => setForm({ ...form, warehouse_sku: e.target.value })} className="bg-[#111] border-[#262626] disabled:opacity-60" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-[#A3A3A3]">Starting stock</Label>
                <Input data-testid="wh-input-stock" type="number" value={form.stock_level} onChange={(e) => setForm({ ...form, stock_level: e.target.value })} className="bg-[#111] border-[#262626]" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-[#A3A3A3]">Product name</Label>
                <Input data-testid="wh-input-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#111] border-[#262626]" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-[#A3A3A3]">Unit cost (USD)</Label>
                <Input data-testid="wh-input-cost" type="number" value={form.unit_cost} onChange={(e) => setForm({ ...form, unit_cost: e.target.value })} className="bg-[#111] border-[#262626]" />
              </div>
            </div>

            {/* Product image */}
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-[#A3A3A3] flex items-center gap-1"><ImageIcon className="h-3 w-3" /> Product image</Label>
              <div className="flex items-start gap-3">
                <ProductThumb src={form.image_url} size={56} />
                <div className="flex-1 space-y-2">
                  <div className="flex items-center gap-2">
                    <input ref={imgRef} type="file" accept="image/png,image/jpeg,image/gif,image/webp" onChange={uploadImage} className="hidden" data-testid="wh-image-file" />
                    <Button data-testid="wh-image-upload-btn" type="button" onClick={() => imgRef.current?.click()} disabled={uploadingImg} variant="outline" className="h-8 bg-[#111] border-[#262626] text-white hover:bg-[#1a1a1a] hover:text-white text-xs">
                      {uploadingImg ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Upload className="h-3.5 w-3.5 mr-1.5" />} Upload
                    </Button>
                    {form.image_url && (
                      <button data-testid="wh-image-clear" type="button" onClick={() => setForm((f) => ({ ...f, image_url: "" }))} className="text-xs text-[#A3A3A3] hover:text-[#EF4444]">Remove</button>
                    )}
                  </div>
                  <Input data-testid="wh-image-url" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="…or paste an image URL" className="bg-[#111] border-[#262626] h-8 text-xs" />
                </div>
              </div>
              <p className="text-[11px] text-[#555]">Leave blank to auto-use the Amazon listing image (best-effort, from a linked SKU's ASIN).</p>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-[#A3A3A3] flex items-center gap-1"><Link2 className="h-3 w-3" /> Linked Amazon / FBA SKUs</Label>
              <LinkedSkuCombobox knownSkus={knownSkus} selected={form.linked_skus} onAdd={addLink} />
              <div className="flex flex-wrap gap-1.5 pt-1">
                {form.linked_skus.map((s) => (
                  <span key={s} data-testid={`wh-chip-${s}`} className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-[#007AFF]/15 text-[#4da3ff] tnum">
                    {s}<button onClick={() => removeLink(s)} className="hover:text-white"><X className="h-3 w-3" /></button>
                  </span>
                ))}
                {!form.linked_skus.length && <span className="text-[#666] text-xs">No SKUs linked yet. 2+ Amazon SKUs can point to this warehouse item.</span>}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-[#A3A3A3] flex items-center gap-1"><Layers className="h-3 w-3" /> Variation group</Label>
              <select data-testid="wh-group-select" value={form.variation_group || ""} onChange={(e) => setForm({ ...form, variation_group: e.target.value || null })} className="w-full h-9 bg-[#111] border border-[#262626] rounded-md px-2 text-sm text-white">
                <option value="">No group</option>
                {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
              <div className="flex items-center gap-2 pt-1">
                <Input data-testid="wh-new-group-input" value={newGroup} onChange={(e) => setNewGroup(e.target.value)} placeholder="Create a new variation group…" className="bg-[#111] border-[#262626] h-8 text-xs" />
                <Button data-testid="wh-new-group-add" type="button" onClick={createGroup} variant="outline" className="h-8 bg-[#111] border-[#262626] text-white hover:bg-[#1a1a1a] hover:text-white text-xs shrink-0"><Plus className="h-3.5 w-3.5 mr-1" /> Add</Button>
              </div>
            </div>
          </div>
          <DialogFooter><Button data-testid="wh-save" onClick={save} className="bg-[#10B981] hover:bg-[#0d9668] text-black">{editing ? "Save changes" : "Add SKU"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {sales.length > 0 && (
        <div className="bg-[#0f0f0f] border border-white/5 rounded-xl overflow-hidden" data-testid="other-sales-table">
          <div className="p-5 border-b border-[#1a1a1a] flex items-center gap-2 flex-wrap"><ShoppingCart className="h-4 w-4 text-[#8B5CF6]" /><h3 className="font-head font-bold text-white">Other-Channel Sales</h3><span className="text-xs text-[#666]">Manual sales (eBay, Shopify, wholesale…) that draw down warehouse stock</span></div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-[#666] text-xs uppercase tracking-wider">
                <th className="px-5 py-3">Channel</th><th className="px-5 py-3">Warehouse SKU</th><th className="px-5 py-3">Date</th>
                <th className="px-5 py-3 text-right">Qty</th><th className="px-5 py-3 text-right">Sale Price</th><th className="px-5 py-3">Note</th><th className="px-5 py-3 text-right">Actions</th>
              </tr></thead>
              <tbody>
                {sales.map((s) => (
                  <tr key={s.id} data-testid={`sale-row-${s.id}`} className="border-t border-[#141414] hover:bg-white/5">
                    <td className="px-5 py-3 text-white font-medium">{s.channel}</td>
                    <td className="px-5 py-3 tnum text-[#4da3ff]">{s.warehouse_sku}</td>
                    <td className="px-5 py-3 text-[#A3A3A3] text-xs tnum">{s.date}</td>
                    <td className="px-5 py-3 text-right tnum text-white">{fmtNum(s.quantity)}</td>
                    <td className="px-5 py-3 text-right tnum text-[#D4D4D4]">
                      {s.sale_price ? `${s.sale_price} ${s.currency}` : "—"}
                      {s.sale_price && s.currency !== currency ? <span className="block text-[11px] text-[#666]" data-testid={`sale-conv-${s.id}`}>≈ {fmtMoney(toDisplay(s.sale_price, s.currency), currency)}</span> : null}
                    </td>
                    <td className="px-5 py-3 text-[#666] max-w-[200px] truncate" title={s.note}>{s.note || "—"}</td>
                    <td className="px-5 py-3 text-right"><button data-testid={`delete-sale-${s.id}`} onClick={() => removeSale(s.id)} className="text-[#A3A3A3] hover:text-[#EF4444]"><Trash2 className="h-4 w-4" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={salesOpen} onOpenChange={setSalesOpen}>
        <DialogContent className="bg-[#0d0d0d] border-[#262626] text-white" data-testid="sale-dialog">
          <DialogHeader>
            <DialogTitle className="font-head flex items-center gap-2"><ShoppingCart className="h-5 w-5 text-[#8B5CF6]" /> Record Other-Channel Sale</DialogTitle>
            <DialogDescription className="text-[#A3A3A3]">Log a sale made outside Amazon (eBay, Shopify, wholesale…). It reduces this warehouse SKU's available stock.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-[#A3A3A3]">Channel</Label>
                <Input data-testid="sale-channel" value={saleForm.channel} onChange={(e) => setSaleForm({ ...saleForm, channel: e.target.value })} placeholder="e.g. eBay, Shopify" className="bg-[#111] border-[#262626]" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-[#A3A3A3]">Warehouse SKU</Label>
                <select data-testid="sale-sku" value={saleForm.warehouse_sku} onChange={(e) => setSaleForm({ ...saleForm, warehouse_sku: e.target.value })} className="w-full h-9 bg-[#111] border border-[#262626] rounded-md px-2 text-sm text-white">
                  <option value="">Select…</option>
                  {items.map((it) => <option key={it.warehouse_sku} value={it.warehouse_sku}>{it.warehouse_sku}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-[#A3A3A3]">Quantity</Label>
                <Input data-testid="sale-qty" type="number" value={saleForm.quantity} onChange={(e) => setSaleForm({ ...saleForm, quantity: e.target.value })} className="bg-[#111] border-[#262626]" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-[#A3A3A3]">Sale price</Label>
                <Input data-testid="sale-price" type="number" value={saleForm.sale_price} onChange={(e) => setSaleForm({ ...saleForm, sale_price: e.target.value })} className="bg-[#111] border-[#262626]" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-[#A3A3A3]">Currency</Label>
                <select data-testid="sale-currency" value={saleForm.currency} onChange={(e) => setSaleForm({ ...saleForm, currency: e.target.value })} className="w-full h-9 bg-[#111] border border-[#262626] rounded-md px-2 text-sm text-white">
                  {SALE_CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-[#A3A3A3]">Date</Label>
                <Input data-testid="sale-date" type="date" value={saleForm.date} onChange={(e) => setSaleForm({ ...saleForm, date: e.target.value })} className="bg-[#111] border-[#262626]" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-[#A3A3A3]">Note</Label>
                <Input data-testid="sale-note" value={saleForm.note} onChange={(e) => setSaleForm({ ...saleForm, note: e.target.value })} className="bg-[#111] border-[#262626]" />
              </div>
            </div>
          </div>
          <DialogFooter><Button data-testid="sale-save" onClick={saveSale} className="bg-[#8B5CF6] hover:bg-[#7c4ee0] text-white">Record Sale</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <WarehouseAnalyticsDrawer sku={analyticsSku} onClose={() => setAnalyticsSku(null)} />
    </div>
  );
}
